import React, { useState } from 'react';
import { 
  Typography, 
  Box, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Card,
  CardContent,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import { 
  Inventory, 
  Close, 
  Warning, 
  CheckCircle, 
  Error,
  TrendingDown,
  AttachMoney,
  ExpandMore,
  Receipt,
  Timeline,
  TrendingUp,
  Pets,
  Restaurant
} from '@mui/icons-material';
import { useData } from '../contexts/DataContext';
import { formatCurrency } from '../utils/currency';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role='tabpanel'
    hidden={value !== index}
    {...other}
  >
    {value === index && (
      <Box sx={{ py: 3 }}>
        {children}
      </Box>
    )}
  </div>
);

const InventoryManagement = () => {
  const { inventory, expenditures, batches } = useData();
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatExpiry = (expiryDate) => {
    if (!expiryDate) return 'Not specified';
    const date = new Date(expiryDate);
    return date.toLocaleDateString();
  };

  const formatLastUpdated = (lastUpdated) => {
    if (!lastUpdated) return 'Not available';
    const date = new Date(lastUpdated);
    return date.toLocaleDateString();
  };

  const getLowStockAlert = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const minQuantity = parseFloat(item.min_quantity) || 0;
    return quantity <= minQuantity;
  };

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    return expiry < today;
  };

  const handleViewItem = (item) => {
    setSelectedItem(item);
    setViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedItem(null);
  };

  const getStockStatus = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const minQuantity = parseFloat(item.min_quantity) || 0;
    
    if (quantity <= minQuantity) {
      return { label: 'Low Stock', color: 'error', icon: <Error /> };
    } else if (quantity <= minQuantity * 2) {
      return { label: 'Medium Stock', color: 'warning', icon: <Warning /> };
    } else {
      return { label: 'Good Stock', color: 'success', icon: <CheckCircle /> };
    }
  };

  const calculateTotalValue = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const costPerUnit = parseFloat(item.cost_per_unit) || 0;
    return quantity * costPerUnit;
  };

  // Get feed expenditures from batches
  const getFeedExpenditures = () => {
    return expenditures.filter(exp => 
      exp.category && exp.category.toLowerCase().includes('food')
    ).map(exp => ({
      ...exp,
      batch_info: batches.find(batch => batch.id === exp.batch_id)
    }));
  };

  // Calculate feed consumption by type
  const getFeedConsumption = () => {
    const feedExpenditures = getFeedExpenditures();
    const consumptionMap = {};

    feedExpenditures.forEach(exp => {
      const feedType = exp.type || 'Unknown Feed';
      const quantity = parseFloat(exp.quantity) || 0;
      const amount = parseFloat(exp.amount) || 0;

      if (!consumptionMap[feedType]) {
        consumptionMap[feedType] = {
          feedType,
          totalQuantity: 0,
          totalAmount: 0,
          usageCount: 0,
          batches: []
        };
      }

      consumptionMap[feedType].totalQuantity += quantity;
      consumptionMap[feedType].totalAmount += amount;
      consumptionMap[feedType].usageCount += 1;
      consumptionMap[feedType].batches.push({
        batch_code: exp.batch_code,
        quantity,
        amount,
        date: exp.expenditure_date || exp.created_at
      });
    });

    return Object.values(consumptionMap);
  };

  // Match inventory items with consumption data
  const getInventoryWithConsumption = () => {
    const feedConsumption = getFeedConsumption();
    return inventory.map(item => {
      if (item.category_name && item.category_name.toLowerCase().includes('feed')) {
        // Try to match inventory item with consumption data
        const matchingConsumption = feedConsumption.find(consumption => 
          item.item_name.toLowerCase().includes(consumption.feedType.toLowerCase()) ||
          consumption.feedType.toLowerCase().includes(item.item_name.toLowerCase())
        );

        return {
          ...item,
          consumption: matchingConsumption
        };
      }
      return item;
    });
  };

  // Calculate inventory statistics
  const inventoryStats = () => {
    const totalItems = inventory.length;
    const lowStockItems = inventory.filter(item => getLowStockAlert(item)).length;
    const expiredItems = inventory.filter(item => isExpired(item.expiry_date)).length;
    const expiringSoonItems = inventory.filter(item => isExpiringSoon(item.expiry_date)).length;
    const totalValue = inventory.reduce((sum, item) => sum + calculateTotalValue(item), 0);
    
    const feedExpenditures = getFeedExpenditures();
    const totalFeedConsumption = feedExpenditures.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

    return {
      totalItems,
      lowStockItems,
      expiredItems,
      expiringSoonItems,
      totalValue,
      totalFeedConsumption
    };
  };

  const stats = inventoryStats();
  const feedConsumption = getFeedConsumption();
  const inventoryWithConsumption = getInventoryWithConsumption();

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant='h5' gutterBottom>
        Inventory Management
      </Typography>

      {/* Enhanced Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Items
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalItems}
                  </Typography>
                </Box>
                <Inventory color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Low Stock Items
                  </Typography>
                  <Typography variant="h4" color={stats.lowStockItems > 0 ? "error.main" : "success.main"}>
                    {stats.lowStockItems}
                  </Typography>
                </Box>
                <TrendingDown color={stats.lowStockItems > 0 ? "error" : "success"} sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Value
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(stats.totalValue)}
                  </Typography>
                </Box>
                <AttachMoney color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Feed Consumed
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {formatCurrency(stats.totalFeedConsumption)}
                  </Typography>
                </Box>
                <Restaurant color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Expired Items
                  </Typography>
                  <Typography variant="h4" color={stats.expiredItems > 0 ? "error.main" : "success.main"}>
                    {stats.expiredItems}
                  </Typography>
                </Box>
                <Error color={stats.expiredItems > 0 ? "error" : "success"} sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {stats.lowStockItems > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {stats.lowStockItems} item(s) are running low on stock. Please reorder soon.
        </Alert>
      )}

      {stats.expiredItems > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {stats.expiredItems} item(s) have expired. Please remove from inventory.
        </Alert>
      )}

      {stats.expiringSoonItems > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {stats.expiringSoonItems} item(s) are expiring within 30 days.
        </Alert>
      )}

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label="Current Inventory" 
            icon={<Inventory />} 
            iconPosition="start"
          />
          <Tab 
            label="Feed Consumption" 
            icon={<Restaurant />} 
            iconPosition="start"
          />
          <Tab 
            label="Usage Analytics" 
            icon={<Timeline />} 
            iconPosition="start"
          />
        </Tabs>

        {/* Current Inventory Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Cost/Unit</TableCell>
                  <TableCell>Total Value</TableCell>
                  <TableCell>Stock Status</TableCell>
                  <TableCell>Feed Usage</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventoryWithConsumption.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="textSecondary">No inventory items found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  inventoryWithConsumption.map((item) => {
                    const stockStatus = getStockStatus(item);
                    const isLowStock = getLowStockAlert(item);
                    const itemExpired = isExpired(item.expiry_date);
                    const itemExpiringSoon = isExpiringSoon(item.expiry_date);
                    
                    return (
                      <TableRow 
                        key={item.id} 
                        sx={{ 
                          bgcolor: itemExpired ? 'rgba(255,0,0,0.1)' : 
                                   isLowStock ? 'rgba(255,165,0,0.1)' : 
                                   itemExpiringSoon ? 'rgba(255,193,7,0.1)' : 'inherit'
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={isLowStock || itemExpired ? "bold" : "normal"}>
                            {item.item_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={item.category_name || item.category || 'Unknown'} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            color={isLowStock ? "error.main" : "textPrimary"}
                            fontWeight={isLowStock ? "bold" : "normal"}
                          >
                            {item.quantity}
                          </Typography>
                        </TableCell>
                        <TableCell>{item.unit}</TableCell>
                        <TableCell>{formatCurrency(parseFloat(item.cost_per_unit) || 0)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(calculateTotalValue(item))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={stockStatus.label} 
                            color={stockStatus.color} 
                            size="small"
                            icon={stockStatus.icon}
                          />
                        </TableCell>
                        <TableCell>
                          {item.consumption ? (
                            <Box>
                              <Typography variant="caption" color="warning.main" fontWeight="bold">
                                Used: {formatCurrency(item.consumption.totalAmount)}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="textSecondary">
                                {item.consumption.totalQuantity} units in {item.consumption.usageCount} batches
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              No usage recorded
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            onClick={() => handleViewItem(item)}
                            variant="outlined"
                            size="small"
                            startIcon={<Inventory />}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Feed Consumption Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Restaurant sx={{ mr: 1 }} />
            Feed Consumption from Batch Expenditures
          </Typography>

          {feedConsumption.length === 0 ? (
            <Alert severity="info">
              No feed consumption data found. Feed expenditures will appear here when you add them to your batches.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {feedConsumption.map((consumption, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card elevation={2}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Restaurant color="warning" sx={{ mr: 2 }} />
                        <Typography variant="h6">{consumption.feedType}</Typography>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Total Quantity</Typography>
                          <Typography variant="h6" color="primary">
                            {consumption.totalQuantity} units
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Total Cost</Typography>
                          <Typography variant="h6" color="warning.main">
                            {formatCurrency(consumption.totalAmount)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Used in Batches</Typography>
                          <Typography variant="h6">
                            {consumption.usageCount}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="textSecondary">Avg Cost/Unit</Typography>
                          <Typography variant="h6">
                            {formatCurrency(consumption.totalAmount / consumption.totalQuantity || 0)}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Accordion sx={{ mt: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle2">View Batch Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {consumption.batches.map((batch, batchIndex) => (
                              <ListItem key={batchIndex}>
                                <ListItemIcon>
                                  <Pets color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={batch.batch_code}
                                  secondary={
                                    <Box>
                                      <Typography variant="caption">
                                        Quantity: {batch.quantity} units | 
                                        Cost: {formatCurrency(batch.amount)}
                                      </Typography>
                                      <br />
                                      <Typography variant="caption" color="textSecondary">
                                        Date: {batch.date ? new Date(batch.date).toLocaleDateString() : 'Not specified'}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Usage Analytics Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Timeline sx={{ mr: 1 }} />
            Inventory Usage Analytics
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Feed Inventory vs Consumption
                  </Typography>
                  
                  {inventory.filter(item => item.category_name && item.category_name.toLowerCase().includes('feed')).map(feedItem => {
                    const matchingConsumption = feedConsumption.find(consumption => 
                      feedItem.item_name.toLowerCase().includes(consumption.feedType.toLowerCase()) ||
                      consumption.feedType.toLowerCase().includes(feedItem.item_name.toLowerCase())
                    );
                    
                    return (
                      <Box key={feedItem.id} sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {feedItem.item_name}
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">Current Stock</Typography>
                            <Typography variant="h6" color="primary">
                              {feedItem.quantity} {feedItem.unit}
                            </Typography>
                            <Typography variant="caption">
                              Value: {formatCurrency(calculateTotalValue(feedItem))}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">Total Consumed</Typography>
                            <Typography variant="h6" color={matchingConsumption ? "warning.main" : "textSecondary"}>
                              {matchingConsumption ? `${matchingConsumption.totalQuantity} units` : 'No usage'}
                            </Typography>
                            <Typography variant="caption">
                              Cost: {matchingConsumption ? formatCurrency(matchingConsumption.totalAmount) : formatCurrency(0)}
                            </Typography>
                          </Grid>
                        </Grid>
                        
                        {matchingConsumption && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="info.main">
                              💡 Usage Rate: {((matchingConsumption.totalQuantity / (parseFloat(feedItem.quantity) + matchingConsumption.totalQuantity)) * 100).toFixed(1)}% of total available
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Consumption Summary
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Total Feed Types Used</Typography>
                    <Typography variant="h4" color="warning.main">
                      {feedConsumption.length}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Total Feed Expenditure</Typography>
                    <Typography variant="h5" color="error.main">
                      {formatCurrency(stats.totalFeedConsumption)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">Most Used Feed Type</Typography>
                    <Typography variant="h6">
                      {feedConsumption.length > 0 
                        ? feedConsumption.reduce((prev, current) => 
                            prev.totalQuantity > current.totalQuantity ? prev : current
                          ).feedType
                        : 'None'
                      }
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" gutterBottom>
                    Recommendations
                  </Typography>
                  
                  <List dense>
                    {stats.lowStockItems > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <Warning color="warning" />
                        </ListItemIcon>
                        <ListItemText primary="Restock low inventory items" />
                      </ListItem>
                    )}
                    
                    {feedConsumption.length > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <TrendingUp color="info" />
                        </ListItemIcon>
                        <ListItemText primary="Monitor feed consumption trends" />
                      </ListItem>
                    )}
                    
                    <ListItem>
                      <ListItemIcon>
                        <Receipt color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Track expenditures for better cost analysis" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
      
      {/* Enhanced View Item Modal */}
      <Dialog open={viewModalOpen} onClose={handleCloseModal} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Inventory Item Details
            </Typography>
            <Button onClick={handleCloseModal} startIcon={<Close />}>
              Close
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Basic Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Item Name</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedItem.item_name}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Category</Typography>
                    <Chip 
                      label={selectedItem.category_name || selectedItem.category || 'Unknown'} 
                      color="primary" 
                      variant="outlined" 
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Supplier</Typography>
                    <Typography variant="body1">{selectedItem.supplier || 'Not specified'}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                    <Typography variant="body1">{selectedItem.notes || 'No notes available'}</Typography>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Stock Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Current Quantity</Typography>
                    <Typography 
                      variant="h5" 
                      fontWeight="bold" 
                      color={getLowStockAlert(selectedItem) ? 'error.main' : 'success.main'}
                    >
                      {selectedItem.quantity} {selectedItem.unit}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Minimum Quantity</Typography>
                    <Typography variant="body1">{selectedItem.min_quantity} {selectedItem.unit}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Stock Status</Typography>
                    <Chip 
                      label={getStockStatus(selectedItem).label} 
                      color={getStockStatus(selectedItem).color} 
                      icon={getStockStatus(selectedItem).icon}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* Feed Consumption Details */}
              {selectedItem.consumption && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <Restaurant sx={{ mr: 1 }} />
                      Feed Consumption from Batches
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2">Total Used</Typography>
                        <Typography variant="h6">{selectedItem.consumption.totalQuantity} units</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2">Total Cost</Typography>
                        <Typography variant="h6">{formatCurrency(selectedItem.consumption.totalAmount)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2">Used in Batches</Typography>
                        <Typography variant="h6">{selectedItem.consumption.usageCount}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2">Avg Unit Cost</Typography>
                        <Typography variant="h6">
                          {formatCurrency(selectedItem.consumption.totalAmount / selectedItem.consumption.totalQuantity || 0)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                      Batch Usage History:
                    </Typography>
                    <List dense>
                      {selectedItem.consumption.batches.map((batch, index) => (
                        <ListItem key={index} sx={{ bgcolor: 'rgba(255,255,255,0.1)', mb: 1, borderRadius: 1 }}>
                          <ListItemText
                            primary={`${batch.batch_code} - ${batch.quantity} units`}
                            secondary={`Cost: ${formatCurrency(batch.amount)} | Date: ${batch.date ? new Date(batch.date).toLocaleDateString() : 'Not specified'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Financial Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Cost per Unit</Typography>
                    <Typography variant="body1">
                      {formatCurrency(parseFloat(selectedItem.cost_per_unit) || 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Total Value</Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">
                      {formatCurrency(calculateTotalValue(selectedItem))}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Dates & Status
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Expiry Date</Typography>
                    <Typography 
                      variant="body1"
                      color={isExpired(selectedItem.expiry_date) ? "error.main" : 
                             isExpiringSoon(selectedItem.expiry_date) ? "warning.main" : "textPrimary"}
                    >
                      {formatExpiry(selectedItem.expiry_date)}
                      {isExpired(selectedItem.expiry_date) && " (Expired)"}
                      {isExpiringSoon(selectedItem.expiry_date) && " (Expiring Soon)"}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Last Updated</Typography>
                    <Typography variant="body1">{formatLastUpdated(selectedItem.last_updated)}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Created</Typography>
                    <Typography variant="body1">{formatLastUpdated(selectedItem.created_at)}</Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryManagement;
